/**
 * API client.
 *
 * One place that knows about the error envelope, the CSRF header and the
 * credentials policy. Everything above this file deals in domain data and never
 * sees an HTTP status.
 *
 * Contract, from `new/docs/api.md`:
 *   ok   -> the parsed body
 *   fail -> an ApiError carrying code, message and structured details
 */

export interface ApiErrorBody {
  error: { code: string; message: string; details: Record<string, unknown> };
}

/** Field-level validation failures, keyed by field name. */
export type FieldErrors = Record<string, string[]>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /**
   * Per-field messages, when the failure was validation.
   *
   * The backend uses both shapes: a rule that names one field throws
   * `details.field`, while the field collector throws `details.fields` with a map
   * of every problem at once. Folding the singular form in here means a
   * single-field error can be rendered under its input instead of as a toast,
   * which is where a person can act on it.
   */
  get fields(): FieldErrors {
    const holder = this.details.fields;
    const collected: FieldErrors =
      holder && typeof holder === "object" && !Array.isArray(holder) ? { ...(holder as FieldErrors) } : {};
    const single = this.details.field;
    if (typeof single === "string" && single) {
      collected[single] = [...(collected[single] ?? []), this.message];
    }
    return collected;
  }

  /** True when the failure is "you are not allowed", worth a different UI. */
  get isPermission(): boolean {
    return this.code === "PERMISSION_DENIED";
  }

  get isUnauthenticated(): boolean {
    return this.code === "UNAUTHENTICATED";
  }
}

const CSRF_COOKIE = "kc_csrf";

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * The CSRF token for this page load.
 *
 * Read from the cookie rather than only remembered from the login response,
 * because a client that reloads or follows a bookmarked link would otherwise
 * lose the token and be unable to make any write until it signed in again.
 * That failure is invisible in tests that never reload, and it makes the whole
 * admin unusable for anyone who refreshes.
 */
export function getCsrfToken(): string | null {
  if (csrfToken) return csrfToken;
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  if (match?.[1]) {
    csrfToken = decodeURIComponent(match[1]);
    return csrfToken;
  }
  return null;
}

/** Adopt the token issued by a sign-in, and keep the cookie copy in step. */
export function adoptCsrfToken(token: string): void {
  setCsrfToken(token);
  document.cookie = `${CSRF_COOKIE}=${encodeURIComponent(token)}; Path=/; SameSite=Strict`;
}

const SAFE = new Set(["GET", "HEAD"]);

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Sent verbatim, bypassing JSON encoding. Used for binary uploads. */
  raw?: BodyInit;
  headers?: Record<string, string>;
  /** Skip the CSRF header. Only for the endpoints that have no session. */
  skipCsrf?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers["content-type"] = "application/json";
  // Explicit headers win, so a binary upload can declare its own type.
  if (options.headers) for (const [key, value] of Object.entries(options.headers)) headers[key] = value;
  if (!SAFE.has(method) && !options.skipCsrf) {
    const token = getCsrfToken();
    if (token) headers["x-csrf-token"] = token;
  }

  const response = await fetch(path, {
    method,
    headers,
    // Session cookie is HttpOnly; credentials must be explicit.
    credentials: "same-origin",
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.raw !== undefined ? { body: options.raw } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiError(response.status, "INVALID_RESPONSE", "服务返回了无法解析的内容");
    }
  }

  if (!response.ok) {
    const body = parsed as ApiErrorBody | null;
    const envelope = body?.error;
    throw new ApiError(
      response.status,
      envelope?.code ?? "UNKNOWN",
      envelope?.message ?? `请求失败（HTTP ${response.status}）`,
      envelope?.details ?? {}
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body: body ?? {} }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT", body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH", body: body ?? {} }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "DELETE" }),

  /**
   * Raw-body upload.
   *
   * The server takes the bytes as the request body with the MIME in a header,
   * rather than multipart — see routes/uploads.ts for why. Returns the parsed
   * JSON envelope, so an upload that the server rejected surfaces as an ApiError
   * with its real reason ("declared type does not match the file content")
   * instead of a generic failure.
   */
  upload: <T>(path: string, file: Blob) =>
    request<T>(path, {
      method: "POST",
      // `raw`, not `body`: `body` is JSON-encoded, which turns a File into the
      // string "{}" and leaves the server sniffing 2 bytes of nonsense. Every
      // upload failed with "file content is not a recognised image or video",
      // which reads like the server's fault and is not.
      raw: file,
      headers: { "content-type": file.type || "application/octet-stream" },
    }),
};

/**
 * The client for endpoints that have no session.
 *
 * Separate from `api` on purpose: the approval-link endpoints are the only
 * unauthenticated writes in the system, and giving them their own object means
 * "this call carries no CSRF token" is visible at the call site instead of
 * buried in a flag. Same-origin is still enforced by the server.
 */
export const publicApi = {
  get: <T>(path: string) => request<T>(path, { method: "GET", skipCsrf: true }),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: "POST", body, skipCsrf: true }),
};

/* ------------------------------------------------------------------------- */
/* Typed shapes used by the UI.                                                  */
/* Deliberately hand-written and only as wide as what is actually rendered, so  */
/* a missing field shows up as a type error instead of an `undefined` crash.   */
/* ------------------------------------------------------------------------- */

export interface Health {
  status: string;
  database: string;
  projectProvider: string;
  notificationChannels: string[];
  time: string;
}

export interface ResourceLink {
  label: string;
  url: string;
}

/** As the admin sees it: everything, including the fields the member view hides. */
export interface AdminResourceNode {
  id: string;
  parentId: string | null;
  title: string;
  description: string;
  type: "COLLECTION" | "RESOURCE" | "WEBSITE" | "DOCUMENT" | "REPOSITORY" | "DEMO";
  url: string;
  links: ResourceLink[];
  accessNote: string;
  /** Empty means public. Non-empty gates the node and its whole subtree. */
  permissionKey: string;
  sortOrder: number;
  archived: boolean;
  children: AdminResourceNode[];
}

/** As a member sees it: protected nodes have no endpoints at all. */
export interface ResourceNode {
  id: string;
  title: string;
  description: string;
  type: "RESOURCE" | "COLLECTION";
  url: string;
  links: ResourceLink[];
  accessNote: string;
  protected: boolean;
  children: ResourceNode[];
}

/** One node opened, including the access secret. Returned only on request. */
export interface ResourceDetail {
  node: ResourceNode;
  /** The extract code. Empty when the node needs no password. */
}

/** A project as the member sees it. */
export interface MemberProject {
  id: string;
  name: string;
  description: string;
  status: string;
  progress: number;
  visibility: string;
  startedAt: string | null;
  dueAt: string | null;
  provider: string;
  hasTasks: boolean;
  hasTimeline: boolean;
  hasMembers: boolean;
  /**
   * When the external tool last changed, or null when it could not be read.
   *
   * `dataUnavailable` is what separates "this project has no tasks" from "KC
   * cannot read this project right now". They render identically otherwise, and
   * the second one looks like bad news about the project rather than about KC.
   */
  providerUpdatedAt?: string | null;
  dataUnavailable?: boolean;
  /**
   * True when the signed-in member leads this project.
   */
  isLead?: boolean;
  /**
   * The signed-in member's role here: `leader`, `member`, `viewer`, or null when
   * they can see the project but have no role in it.
   *
   * This is what splits the member's project list into "我负责的" and "我只看着的".
   * The client must not re-derive it: `isLead` plus a grant lookup is two requests'
   * worth of state to keep in sync, and this field is the answer to one question.
   */
  myRole?: "leader" | ProjectRole | null;
}

/** A task as the member page sees it, including who owns it. */
export interface MemberTask {
  id: string;
  title: string;
  status: "todo" | "in_progress" | "blocked" | "done";
  assigneeIds: string[];
  dueAt: string | null;
}

/**
 * The states a task can be moved to.
 *
 * Wider than the four the read side groups into, because a transition is named by
 * what the member is doing rather than by which bucket it lands in: submitting for
 * review and marking done are different intentions that used to be indistinguishable.
 */
export type TaskTransition = "todo" | "in_progress" | "blocked" | "review" | "done" | "cancelled";

/** Somebody who may be added to an area, with both id namespaces. */
export interface WritableTeamMember {
  memberId: string;
  name: string;
  role: ProjectRole;
  toolMemberId: string | null;
}

/** An area and the tool-side members responsible for it. */
export interface MemberArea {
  id: string;
  name: string;
  description: string;
  ownerIds: string[];
  taskCount: number;
  rev: string | null;
}

export interface ProjectDetail extends MemberProject {
  tasks: MemberTask[];
  timeline: { id: string; kind: string; title: string; body: string; occurredAt: string; authorId: string | null }[];
  members: { id: string; name: string; role: string }[];
}

/**
 * A card as the public API returns it.
 *
 * Spelled out rather than inherited, because the difference is the point: the
 * workspace link is internal and the public payload does not carry it. Extending
 * the admin shape here would say otherwise in the type system and quietly let the
 * id back into public-facing code.
 */
export interface PublicProject extends Omit<ShowcaseProject, "organizationProjectId"> {}

export interface Achievement {
  id: string;
  title: string;
  type: string;
  description: string;
  achievedOn: string;
  projectId: string;
  imageUrl: string;
  linkUrl: string;
}

export interface SiteSettings {
  clubName: string;
  englishName: string;
  heroTitle: string;
  heroDescription: string;
  contactEmail: string;
}

export interface PublicContent {
  settings: SiteSettings;
  projects: PublicProject[];
  achievements: Achievement[];
  resources: ResourceNode[];
}

/**
 * A department as the admin API returns it.
 *
 * The prefix and the sort order were missing here even though both are stored and
 * both are sent — the type was written from the *public* shape, which omits them
 * on purpose. Reading `accountPrefix` off this type therefore failed to compile,
 * which is the good case; the bad case would have been a `as any` creeping in
 * next to it.
 */
export interface Department {
  id: string;
  name: string;
  description: string;
  /** Uppercase letters and digits; usernames are prefix + student id. */
  accountPrefix: string;
  isOpen: boolean;
  sortOrder: number;
}

/** The public form's view: no prefix, no ordering. */
export type PublicDepartment = Pick<Department, "id" | "name" | "description" | "isOpen">;

export interface MemberSession {
  account: { id: string; username: string; mustSetPassword: boolean };
  principal: {
    memberId: string | null;
    displayName: string;
    departmentId: string | null;
    permissions: string[];
    hasBackendRole: boolean;
  };
}

export interface AdminSession {
  account: { id: string; username: string; displayName: string };
  role: string;
  panels: string[];
  departmentIds: string[];
}

export interface AdminAccount {
  id: string;
  username: string;
  email: string;
  status: "active" | "disabled" | "pending";
  mustSetPassword: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Member {
  id: string;
  accountId: string;
  name: string;
  studentId: string;
  className: string;
  email: string;
  contact: string;
  departmentId: string;
  permissions: string[];
  createdAt: string;
}

export interface JoinApplication {
  id: string;
  name: string;
  studentId: string;
  className: string;
  contact: string;
  email: string;
  departmentId: string;
  motivation: string;
  status: string;
  createdAt: string;
  /** The review opened for this application, so the screen can decide it here. */
  approvalRequestId: string | null;
  approvalStatus: string | null;
  /** Set once the application has actually produced a member. */
  memberId?: string | null;
}

export interface UsageRequest {
  id: string;
  memberId: string;
  departmentId: string;
  kind: "material" | "fund";
  targetId: string;
  targetName: string;
  quantity: string | null;
  amount: string | null;
  unit: string;
  purpose: string;
  status: string;
  approvalRequestId: string | null;
  reviewedVia: string | null;
  reviewNote: string | null;
  createdAt: string;
}

export interface ApprovalStep {
  id: string;
  stepOrder: number;
  label: string;
  requiredRole: string | null;
  requiredPanels: string[];
  requiredDepartments: string[];
  status: "waiting" | "active" | "approved" | "rejected" | "skipped";
  decidedBy: string | null;
  decidedAt: string | null;
}

export interface ApprovalDecision {
  id: string;
  requestId: string;
  stepId: string;
  decision: "approved" | "rejected";
  note: string;
  actorId: string;
  /**
   * Mirrors the port, not the UI's taste: the backend records how the decision
   * actually arrived, so renaming `api` to `admin` here would have made the
   * audit trail disagree with the screen.
   */
  decidedVia: "api" | "email" | "system";
  decidedAt: string;
}

export interface ApprovalRequest {
  id: string;
  subjectType: string;
  subjectId: string;
  requesterId: string | null;
  departmentId: string | null;
  title: string;
  summary: string;
  status: "pending" | "approved" | "rejected" | "cancelled" | "superseded";
  createdAt: string;
  updatedAt: string;
  /**
   * When the chain reached a terminal state.
   *
   * Named `completedAt` because that is what the API sends. This type used to
   * declare a `decidedAt` that no endpoint has ever returned, so the "settled
   * at" line silently rendered as nothing at all.
   */
  completedAt: string | null;
  steps: ApprovalStep[];
}

/**
 * The decision trail, which the listing deliberately does not carry.
 *
 * One extra query per row would be the price of inlining it, and an operator
 * looks at one request at a time — so it is fetched when the row opens.
 */
export interface ApprovalDetail {
  request: ApprovalRequest;
  decisions: ApprovalDecision[];
}

export interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  category: string;
  unit: string;
  unitCost: number;
  /** Row and column are required together by the kernel; both empty means no location. */
  storageLocation: { container: string; label: string; row: string; column: string } | null;
  notes: string;
  status: "active" | "archived";
  createdAt: string;
  updatedAt: string;
}

/** On-hand / reserved / available. Quantity is derived, never a stored column. */
export interface InventoryBalance {
  item: InventoryItem;
  quantity: number;
  reserved: number;
  available: number;
}

export interface InventoryList {
  items: InventoryBalance[];
  movements: InventoryMovement[];
}

export interface InventoryMovement {
  id: string;
  itemId: string;
  type: "IN" | "OUT" | "ADJUST";
  quantity: number;
  reason: string;
  note: string;
  usageRequestId: string | null;
  occurredAt: string;
  actorId: string | null;
}

export interface FundAccount {
  id: string;
  name: string;
  currency: string;
  /** Ceiling for allocations; 0 means unbounded. */
  budget: number;
  status: "active" | "archived";
  createdAt: string;
  updatedAt: string;
}

export interface FundBalance {
  account: FundAccount;
  balance: number;
  reserved: number;
  available: number;
}

export interface FundList {
  accounts: FundBalance[];
  transactions: FundTransaction[];
}

export interface FundTransaction {
  id: string;
  accountId: string;
  amount: number;
  direction: "in" | "out";
  reason: string;
  usageRequestId: string | null;
  occurredAt: string;
  actorId: string | null;
}

export interface AuditPage {
  items: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditEvent {
  id: string;
  occurredAt: string;
  actorId: string | null;
  actorLabel: string;
  action: string;
  entityType: string;
  entityId: string;
  meta: Record<string, unknown>;
}

/**
 * What a member may do in a project.
 *
 * Mirrors `PROJECT_ROLES` in the ports package. `leader` is not here: the leader is
 * a single column on the project, so there is never a choice to make about it in a
 * role picker.
 */
export type ProjectRole = "member" | "viewer";

/** An area as the project tool reports it, including its write token. */
export interface ProviderArea {
  id: string;
  name: string;
  description: string;
  /** Tool-side member ids responsible for it. */
  ownerIds: string[];
  taskCount?: number;
  rev?: string | null;
}

/**
 * A self-hosted repository as the backend reports it.
 *
 * Size and last commit come from the server on purpose: they are facts about a git
 * repository, and a page that estimated them would be showing something other than
 * what is on disk.
 */
export interface ProjectRepository {
  slug: string;
  barePath: string;
  workPath: string | null;
  bytes: number | null;
  lastCommit: { sha: string; at: string; subject: string } | null;
  /** A checkout exists but the project tool cannot read it. */
  readable: boolean;
  projectId: string | null;
  projectName: string | null;
}

/** One tracked file in a repository, as git sees it at HEAD. */
export interface RepositoryFile {
  path: string;
  kind: "file" | "dir";
  bytes: number | null;
}

/**
 * A file's contents.
 *
 * `text` is null for a binary or oversized file, with `binaryReason` saying which —
 * so the screen can say "binary, not shown" instead of rendering mojibake.
 */
export interface RepositoryFileContent {
  path: string;
  text: string | null;
  binaryReason: "binary" | "too-large" | null;
  bytes: number;
}

export interface ProjectGrant {
  id: string;
  projectId: string;
  memberId: string;
  role: ProjectRole;
  grantedAt: string;
  grantedBy: string | null;
}

/** One row of the project workspace, as the admin screen sees it. */
export type ProjectPortalRow = ProjectPortal["projects"][number];

/**
 * What a member can actually request.
 *
 * `available` is on-hand minus what pending requests already committed, so two
 * members cannot each be told the last board is theirs.
 */
export interface MemberCatalog {
  materials: {
    id: string;
    name: string;
    unit: string;
    unitCost: number;
    available: number;
    location: string;
  }[];
  accounts: { id: string; name: string; available: number }[];
  principal: { departmentId: string | null };
}

/** A public card, paired with the workspace it presents. */
export interface ShowcaseLink {
  showcaseId: string;
  title: string;
  visibility: "public" | "internal" | "private";
  organizationProjectId: string | null;
}

export interface ProjectPortal {
  provider: { name: string; capabilities: string[]; limitations: string[] };
  /** Candidates for project lead. A lead may send announcements to their team. */
  members: { id: string; name: string; departmentId: string }[];
  projects: {
    id: string;
    name: string;
    visibility: string;
    status: string;
    /** The backend's own id, e.g. ProjectTool's `PRJ-…`. */
    externalId: string | null;
    /** The backend's on-disk slug, e.g. the checkout directory name. */
    slug?: string | null;
    /** Which adapter answered, e.g. `pjt`. */
    provider: string;
    /** Completion reported by the external tool, 0-100. */
    progress: number;
    /** The member who may send announcements for this project. */
    leadMemberId?: string | null;
    /** When the external tool last changed; null when it could not be read. */
    providerUpdatedAt?: string | null;
    /** True when the provider could not be read for this project at all. */
    dataUnavailable?: boolean;
  }[];
}

/** The showcase project, as the content service models it. */
export interface ShowcaseProject {
  id: string;
  /** The project workspace this card presents, or null for a card with none. */
  organizationProjectId: string | null;
  title: string;
  category: string;
  description: string;
  tags: string[];
  color: string;
  posterUrl: string;
  videoUrl: string;
  links: { label: string; url: string }[];
  visibility: "public" | "internal" | "private";
  sortOrder: number;
  updatedAt: string;
}
